import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { supabase } from '../supabaseClient';

const Hero = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const sendConfirmationEmail = async (email) => {
    // In a real production environment with Supabase, you would typically use an Edge Function 
    // or a Trigger to call an external email service (like Resend, SendGrid, etc.).
    // For now, we simulate the "Sending" part on the client side for visual feedback,
    // while the data is securely stored in the Supabase database.
    
    // Example of how you would call a Supabase Edge Function:
    // const { data, error } = await supabase.functions.invoke('send-waitlist-email', { body: { email } });

    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`
          [MOCK EMAIL SERVICE] 
          To: ${email}
          Subject: Welcome to the Faith Connect Waitlist!
          Body: 
          Hi there,
          
          Thank you for joining the Faith Connect family! We are thrilled to have you with us.
          
          You have been successfully added to our priority waitlist. As promised, you will be among the first to know when we launch our beta testing phase.
          
          Faith Connect is built to be a safe, uplifting sanctuary for believers. We can't wait to show you what we've been building.
          
          Blessings,
          The Faith Connect Team
        `);
        resolve(true);
      }, 1000);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (email) {
      setIsSubmitting(true);
      
      try {
        // 1. Insert into Supabase
        const { error } = await supabase
          .from('waitlist')
          .insert([{ email }]);

        if (error) {
          // Check for unique violation (Postgres error code 23505)
          if (error.code === '23505') {
            throw new Error('This email is already on the waitlist!');
          } else {
            throw error;
          }
        }

        // 2. Trigger email sending (simulated or real function call)
        await sendConfirmationEmail(email);
        
        // 3. Show success UI
        setIsSubmitting(false);
        setShowPopup(true);
        setEmail('');
      } catch (error) {
        console.error('Error joining waitlist:', error);
        setErrorMessage(error.message || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  return (
    <>
      <header id="hero" className="hero">
        <div className="blob-bg blob-1"></div>
        <div className="blob-bg blob-2"></div>
        
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <span className="hero-subtitle">Introducing Faith Connect</span>
              <h1>Your Spiritual Sanctuary.</h1>
              <p>
                A new era of social connection built on Christian values. 
                Safe, moderated, and uplifting. Be the first to experience it.
              </p>
              
              <div className="subscribe-form">
                <form onSubmit={handleSubmit}>
                  <div className="input-group">
                    <input 
                      type="email" 
                      placeholder="Enter your email address..." 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                    <button type="submit" className="btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                    </button>
                  </div>
                  
                  {errorMessage && (
                    <div style={{ marginTop: '12px', color: '#ef4444', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                      {errorMessage}
                    </div>
                  )}

                  <p className="form-note" style={{ marginTop: '16px', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                    Join 2,000+ others waiting for early access.
                  </p>
                </form>
              </div>
            </div>
            
            <div className="hero-visual">
              <div style={{
                width: '100%',
                maxWidth: '380px',
                aspectRatio: '9/19',
                background: 'var(--bg-card)',
                borderRadius: '40px',
                border: '8px solid var(--text-main)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Fake Phone UI */}
                <div style={{
                  height: '30px',
                  background: 'var(--bg-surface)',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}>
                  <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#cbd5e1' }}></div>
                </div>
                
                <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ width: '60%', height: '8px', background: 'var(--border-color)', borderRadius: '4px' }}></div>
                  <div style={{ width: '100%', height: '140px', background: 'var(--bg-surface)', borderRadius: '16px' }}></div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--primary-light)', opacity: 0.2 }}></div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ width: '80%', height: '8px', background: 'var(--border-color)', borderRadius: '4px' }}></div>
                      <div style={{ width: '50%', height: '8px', background: 'var(--border-color)', borderRadius: '4px' }}></div>
                    </div>
                  </div>
                  <div style={{ width: '100%', height: '100px', background: 'var(--bg-surface)', borderRadius: '16px', marginTop: 'auto' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Success Popup */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-card" onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon">
              <Check size={40} strokeWidth={3} />
            </div>
            <h3>Welcome to the Family!</h3>
            <p>
              You've been successfully added to our waitlist. 
              We've sent a confirmation email to <strong>your inbox</strong> with more details.
              We'll notify you as soon as beta testing opens!
            </p>
            <button className="popup-close-btn" onClick={closePopup}>
              Awesome, thanks!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Hero;
